import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DisplayUserEmail, GalleryDto, GalleryMemberAccess, GalleryMemberDto } from '@nusign/picflow-backend-types';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { GalleryService } from '../../../services/gallery.service';
import { OrganizationService } from '../../../services/organization.service';
import { StateService } from '../../../services/state.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { AccessItemBodyComponent } from '../access-item-body/access-item-body.component';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemComponent } from '../access-item/access-item.component';

@Component({
  selector: 'app-gallery-member-access-item',
  imports: [
    GalleryShareContextComponent,
    AccessItemComponent,
    AccessItemHeaderComponent,
    AccessItemBodyComponent,
    AccessItemCtaComponent,
    BadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gallery-member-access-item.component.html',
  styleUrl: './gallery-member-access-item.component.scss',
})
export class GalleryMemberAccessItemComponent {
  private galleryMemberService = inject(GalleryMemberService);
  private organizationService = inject(OrganizationService);
  private galleryService = inject(GalleryService);
  private router = inject(Router);
  private state = inject(StateService);
  gallery = input.required<GalleryDto>();
  member = input.required<GalleryMemberDto & { user: DisplayUserEmail }>();
  galleryOwner = input.required<DisplayUserEmail>();
  memberAccess = input.required<GalleryAccessLevel>();
  galleryPermissions = input.required<GalleryDto['permissions']>();
  isTeamMember = input.required<boolean>();
  private teamMembers = toSignal(
    toObservable(this.isTeamMember).pipe(
      filter(Boolean),
      switchMap(() => this.organizationService.getMembers())
    )
  );

  isMe = computed(() => {
    const member = this.member();
    if (!member) return;
    return member.user.id === this.state.user.id;
  });

  isMemberTeamMember = computed(() => {
    const member = this.member();
    const teamMembers = this.teamMembers();
    if (!teamMembers || !member) return;
    return teamMembers.some(teamMember => teamMember.id === member.user.id);
  });

  allowOwnerChange = computed(() => {
    const member = this.member();
    const teamMembers = this.teamMembers();
    const isMemberTeamMember = this.isMemberTeamMember();
    const gallery = this.gallery();
    if (!teamMembers || !member || !isMemberTeamMember || !gallery) return false;
    if (gallery.owner === this.state.user.id) return true;
    if (this.state.activeTenantRole !== 'member') return true;
    return false;
  });

  memberAccessLevels = computed<(GalleryMemberAccess | 'leave' | 'remove' | 'make-owner')[]>(() => {
    const member = this.member();
    const allowOwnerChange = this.allowOwnerChange();
    if (!member) return;

    if (member.user.id === this.state.user.id) return [member.access, 'leave'];
    const accessLevels: (GalleryMemberAccess | 'remove' | 'make-owner')[] = ['edit', 'review', 'view_only', 'remove'];
    if (allowOwnerChange) accessLevels.splice(accessLevels.length - 1, 0, 'make-owner');
    return accessLevels;
  });

  changeGalleryOwner() {
    const owner = this.galleryOwner();
    const member = this.member();
    return this.galleryMemberService
      .inviteMember(owner.email, this.gallery().id, 'edit', false, '')
      .pipe(
        switchMap(() => this.galleryService.changeGalleryOwner(this.gallery().id, member.user.id)),
        tap(() => this.changeMemberAccessLevel('remove'))
      )
      .subscribe();
  }

  changeMemberAccessLevel(newAccessLevel: GalleryMemberAccess | 'leave' | 'remove' | 'make-owner') {
    if (newAccessLevel === 'make-owner') {
      this.changeGalleryOwner();
    } else if (newAccessLevel === 'remove' || newAccessLevel === 'leave') {
      this.galleryMemberService
        .removeMember(this.member().id)
        .pipe(
          catchError(err => {
            if (!(err instanceof HttpErrorResponse)) throw err;
            if (err.status !== 404 && err.status !== 403 && err.status !== 401) throw err;
            // Member was already removed
            return of(undefined);
          })
        )
        .subscribe(() => {
          if (newAccessLevel === 'leave') {
            this.router.navigate(['/a', 'home']);
          }
        });
    } else {
      this.galleryMemberService.updateMember(this.member().id, newAccessLevel).subscribe();
    }
  }
}
